<template>
  <div v-if="showComponent" class="customer-type-container">
    <template v-if="showLabels">
      <horizontal-line v-if="showHorizontalLine" class="horizontalLine" />
      <h5>
        {{ i18n.t('/CustomerType/BlockTitle') }}
        <button @click="showInfoModal = true">
          <icon icon="info-plain" large />
        </button>
      </h5>
    </template>
    <loader v-if="isLoading" />
    <div v-else>
      <form-grouped-select
        v-if="showGroupedSelectForm"
        id="customerType"
        model-value=""
        title="Customer Type"
        :show-label="false"
        :option-groups="optionGroups"
        :no-value-text="i18n.t('/CustomerType/PlaceholderText')"
        @change="updateCustomerType"
      />
      <p v-else>
        <strong>{{ selectedCustomerType.GroupName }} -</strong>
        {{ selectedCustomerType.Name }}

        <button v-if="isEditable" class="editButton" @click="editCustomerType">
          <icon icon="pencil" width="20" />
        </button>
      </p>
      <horizontal-line class="horizontalLine" />
    </div>
    <modal
      v-if="showInfoModal"
      :title="i18n.t('/CustomerType/Info/Title')"
      @close="showInfoModal = false"
    >
      {{ i18n.t('/CustomerType/Info/Text') }}
    </modal>
  </div>
</template>

<script>
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import FormGroupedSelect from '@/components/generic/presentational/atoms/formGroupedSelect/FormGroupedSelect.vue'
import CustomerTypes from '@/api/server/CustomerTypes'
import HorizontalLine from '@/components/generic/presentational/atoms/horizontalLine/HorizontalLine.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import { mapGetters, mapState } from 'vuex'

export default {
  components: {
    FormGroupedSelect,
    Icon,
    Loader,
    HorizontalLine,
    Modal,
  },
  props: {
    showLabels: {
      type: Boolean,
      default: true,
    },
    isB2b: {
      type: Boolean,
      default: false,
    },
    isPurchaseOrderPage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadingCustomerType: false,
      isLoading: true,
      isEditable: true,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showComponent: false,
      showInfoModal: false,
    }
  },
  computed: {
    ...mapGetters({
      customerTypes: 'customerTypes/all',
      isQuoteLoading: 'quote/loading',
      hasOwner: 'quote/hasOwner',
      quoteStatusIsSalesOrder: 'quote/quoteStatusIsSalesOrder',
    }),
    ...mapState({
      selectedCustomerType(state, getters) {
        const getterName = this.isPurchaseOrderPage ? 'purchaseOrder' : 'quote'
        return getters[`${getterName}/customerType`]
      },
      purchaseOrder: ['purchaseOrder'],
    }),
    optionGroups() {
      return this.customerTypes?.map((customerTypeGroup) => {
        return {
          name: customerTypeGroup.GroupName,
          options: customerTypeGroup.CustomerTypes.map((type) => {
            return { value: type.Id, name: type.Name }
          }),
        }
      })
    },
    showHorizontalLine() {
      return !this.isB2b && !this.isPurchaseOrderPage
    },
    nameSpace() {
      return this.isPurchaseOrderPage ? 'purchaseOrder' : 'quote'
    },
    showGroupedSelectForm() {
      return this.isPurchaseOrderPage
        ? !this.selectedCustomerType?.Name
        : !this.selectedCustomerType?.Name && this.isEditable
    },
  },
  watch: {
    isQuoteLoading: {
      handler(value) {
        this.isLoading = value
      },
      immediate: true,
    },
    hasOwner: {
      handler(value) {
        this.showComponent = this.isPurchaseOrderPage ? this.isPurchaseOrderPage : value
      },
      immediate: true,
    },
  },
  async mounted() {
    this.initCustomerTypes()
  },
  async created() {
    this.setInitialValues()
  },
  methods: {
    async updateCustomerType(selectedOption) {
      await this.$store.dispatch(`${this.nameSpace}/setCustomerTypeId`, selectedOption)

      //todo make it common behavior for every page
      if (this.isPurchaseOrderPage) {
        const customerTypeFromDropDown = this.findCustomerTypeById(selectedOption)
        await this.$store.dispatch(`${this.nameSpace}/setCustomerType`, customerTypeFromDropDown)
      }
    },
    async editCustomerType() {
      await this.$store.dispatch(`${this.nameSpace}/setCustomerTypeId`, 0)
      this.initCustomerTypes()
    },
    async initCustomerTypes() {
      if (!this.selectedCustomerType?.Id && !this.customerTypes?.length) {
        const types = await CustomerTypes.getCustomerTypes()
        this.$store.dispatch('customerTypes/setCustomerTypes', types)
      }
    },
    findCustomerTypeById(id) {
      const result = this.customerTypes
        ?.flatMap((obj) =>
          obj.CustomerTypes.map((customerType) => ({
            Id: customerType.Id,
            GroupName: obj.GroupName,
            Name: customerType.Name,
          }))
        )
        .find((obj) => obj.Id === Number(id))
      return result || null
    },
    setInitialValues() {
      this.isEditable = this.isPurchaseOrderPage
        ? this.purchaseOrder?.purchaseOrder?.Status !== 'PURCHASEORDER'
        : !this.quoteStatusIsSalesOrder
    },
  },
}
</script>

<style>
.horizontalLine {
  margin-top: 1em;
}

.editButton {
  vertical-align: middle;
}

.customer-type-container {
  margin-bottom: 20px;
}
</style>
